import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { PanelRightClose, PanelRightOpen, SlidersHorizontal } from "lucide-react";
import { territorySummary, useGeographyFilter } from "@/contexts/GeographyFilterContext";
import { ModuleMapUiStubProvider } from "@/components/module-map/ModuleMapUiStubProvider";
import type { Geometry } from "@/types/api-v2";
import { DEFAULT_MVT_FILTERS, DEFAULT_MVT_LAYER_SETTINGS } from "@/lib/map-ui-layers-persist";

const MapLibreMap = lazy(() => import("@/components/map-libre/MapLibreMap"));
const EMPTY_THEMATIC_REGION_FEATURES: GeoJSON.Feature[] = [];

export type ModuleWorkspaceMapPanelProps = {
  /** Заголовок колонки карты. */
  title?: string;
  /** Оверлей геометрий поверх MVT (контуры модуля). */
  overlayGeometries?: Geometry[];
  /** Синтетический selectedObject для подсветки оверлея (как у РД). */
  selectedObject?: unknown | null;
  /** Клик по объекту ОИ (MVT). */
  onMvtSelect?: (payload: { objectId: number; geometryId?: number }) => void;
  /** Внешний запрос: показать конкретный контур (preview). */
  previewGeometry?: Geometry | null;
  previewNonce?: number;
  onPreviewConsumed?: () => void;
  collapsed?: boolean;
  onRequestCollapse?: () => void;
  onRequestExpand?: () => void;
  /** Панель инструментов над картой (draw strip РД и т.п.). */
  toolbar?: ReactNode;
  /** Активный инструмент карты (select / draw-*). */
  activeTool?: string | null;
  onActiveToolChange?: (tool: string | null) => void;
  isDrawing?: boolean;
  pendingSketchFeature?: GeoJSON.Feature | null;
  onDrawingComplete?: (drawing: { geoJSON?: GeoJSON.Feature }) => void;
  /**
   * Thematic region overlay (контракт map-client/thematic-region-overlay).
   * Не идут в слой selected-geometries.
   */
  thematicRegionFeatures?: GeoJSON.Feature[];
  /** @deprecated Используйте thematicRegionFeatures */
  moduleContourFeatures?: GeoJSON.Feature[];
  selectedThematicFeatureId?: string | null;
  onThematicFeatureClick?: (featureId: string) => void;
  /** Клик мимо слоёв карты — снять thematic/object selection (модуль). */
  onClearObjectSelection?: () => void;
  /** Слой точек задач и инцидентов. На shell других модулей выключен. */
  workItemsVisible?: boolean;
  /** Клик по маркеру задачи на карте модуля. */
  onWorkItemSelect?: (workItemId: number) => void;
};

/**
 * Общий shell встроенной карты для workspace L2-модулей.
 * Доменная логика (draw РД, bind геометрий) остаётся в обёртках модулей.
 */
export function ModuleWorkspaceMapPanel({
  title = "Карта объектов ОИ",
  overlayGeometries = [],
  selectedObject = null,
  onMvtSelect,
  previewGeometry = null,
  previewNonce = 0,
  onPreviewConsumed,
  collapsed = false,
  onRequestCollapse,
  onRequestExpand,
  toolbar,
  activeTool: activeToolProp,
  onActiveToolChange,
  isDrawing = false,
  pendingSketchFeature = null,
  onDrawingComplete,
  thematicRegionFeatures,
  moduleContourFeatures,
  selectedThematicFeatureId = null,
  onThematicFeatureClick,
  onClearObjectSelection,
  workItemsVisible = false,
  onWorkItemSelect,
}: ModuleWorkspaceMapPanelProps) {
  const { territories, setSheetOpen } = useGeographyFilter();
  const thematicFeatures =
    thematicRegionFeatures ?? moduleContourFeatures ?? EMPTY_THEMATIC_REGION_FEATURES;
  const [internalTool, setInternalTool] = useState<string | null>("select");
  const activeTool = activeToolProp ?? internalTool;
  const setActiveTool = onActiveToolChange ?? setInternalTool;
  const onPreviewConsumedRef = useRef(onPreviewConsumed);
  onPreviewConsumedRef.current = onPreviewConsumed;

  useEffect(() => {
    if (!previewGeometry?.geojson || previewNonce < 1) return;
    window.dispatchEvent(
      new CustomEvent("focusGeometryOnMap", {
        detail: { geometry: previewGeometry, source: "highlight" },
      }),
    );
    onPreviewConsumedRef.current?.();
  }, [previewGeometry, previewNonce]);

  /**
   * Sticky highlight снимаем только когда нет и selectedObject, и overlay.
   * Иначе при выборе страны (selectedObject ещё null, geometries грузятся) clear срывал fitBounds.
   * Явный deselect в go-workspace дополнительно шлёт clearMapGeometryHighlight.
   */
  useEffect(() => {
    if (selectedObject != null || overlayGeometries.length > 0) return;
    window.dispatchEvent(new CustomEvent("clearMapGeometryHighlight"));
  }, [selectedObject, overlayGeometries.length]);

  if (collapsed) {
    return (
      <div className="flex h-full w-full flex-col items-center border-l border-border bg-muted/30 py-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Показать карту"
          aria-label="Показать карту"
          onClick={() => onRequestExpand?.()}
        >
          <PanelRightOpen className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 w-full min-w-0 flex-col border-l border-border bg-background">
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-border px-2">
        <span className="truncate text-xs font-medium text-muted-foreground">{title}</span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={territories.length > 0 ? "secondary" : "ghost"}
            size="sm"
            className="h-7 max-w-[160px] gap-1 px-2 text-xs"
            title="Географические фильтры"
            onClick={() => setSheetOpen(true)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{territorySummary(territories)}</span>
          </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Свернуть карту"
          aria-label="Свернуть карту"
          onClick={() => onRequestCollapse?.()}
        >
          <PanelRightClose className="h-4 w-4" />
        </Button>
        </div>
      </div>

      {toolbar}

      <div className="relative min-h-0 flex-1">
        <ModuleMapUiStubProvider setActiveTool={setActiveTool}>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Загрузка карты...
              </div>
            }
          >
            <MapLibreMap
              selectedObject={selectedObject}
              geometries={overlayGeometries}
              activeTool={activeTool ?? "select"}
              isDrawing={isDrawing}
              pendingSketchFeature={pendingSketchFeature}
              onDrawingComplete={onDrawingComplete ?? (() => {})}
              thematicRegionFeatures={thematicFeatures}
              selectedThematicFeatureId={selectedThematicFeatureId}
              onThematicFeatureClick={onThematicFeatureClick}
              onClearObjectSelection={onClearObjectSelection}
              onMvtSelect={onMvtSelect}
              useMvt
              mvtFilters={DEFAULT_MVT_FILTERS}
              mvtLayerSettings={DEFAULT_MVT_LAYER_SETTINGS}
              showBaseLayerSwitcher
              cadastralLayersVisible={false}
              telegramIncidentsVisible={false}
              workItemsVisible={workItemsVisible}
              onWorkItemSelect={onWorkItemSelect}
            />
          </Suspense>
        </ModuleMapUiStubProvider>
      </div>
    </div>
  );
}
